/*
  Runtime accessibility safety net (see ACCESSIBILITY.md).

  Components should be accessible in their own source: ActionButton,
  Components/Form, ConfirmDialog and the list components already are. This
  module covers what that cannot reach: hundreds of legacy `<Label>` /
  `<Input>` pairs that were never wired together, icon-only buttons and links,
  third-party widgets (react-select, flatpickr, reactstrap tabs / modals) and
  markup rendered dynamically.

  It only ever ADDS missing semantics. It never overrides a name, role or id
  that the component set itself, and it never changes layout or behaviour:

    - decorative icon glyphs            -> aria-hidden
    - <label> without `for`             -> linked to the control that follows it
    - unnamed inputs                    -> column header / placeholder / name
    - icon-only buttons and links       -> name taken from the icon
    - <th> without scope                -> scope="col" | "row"
    - modal / offcanvas without a name  -> aria-labelledby the title
    - nav-tabs without roles            -> tablist / tab / tabpanel
    - <img> without alt, <iframe> without title
    - `title` tooltips                  -> also shown on keyboard focus

  Auto-generated names are tagged `data-a11y-auto` so they are refreshed
  instead of going stale, and are dropped when the element gets a real name.
*/

const CONTROL =
  'input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="reset"]):not([type="image"]), select, textarea';
const ICON = 'i[class*="ri-"], i[class*="bx-"], i[class*="mdi-"]';
const INTERACTIVE =
  'button, a[href], [role="button"], [role="menuitem"], [role="tab"], summary';

// Remix icon name (without ri- prefix / -line / -fill) -> accessible name.
// Order matters: first match wins, so specific names come first.
const ICON_LABELS = [
  [/delete-bin|delete-back|close-circle|trash/, 'Delete'],
  [/edit|pencil|ball-pen/, 'Edit'],
  [/eye-off|eye-close/, 'Hide'],
  [/^eye|file-search|search-eye|survey/, 'View'],
  [/search/, 'Search'],
  [/^close|^cross|x-mark/, 'Close'],
  [/^add|plus/, 'Add'],
  [/download|save-3|file-download/, 'Download'],
  [/upload/, 'Upload'],
  [/save|floppy/, 'Save'],
  [/more|^menu-?[0-9]*$|^menu-fold|^menu-unfold|apps/, 'More actions'],
  [/refresh|restart|loop|reset|reload|repeat/, 'Refresh'],
  [/settings|tools|equalizer|sliders/, 'Settings'],
  [/filter|funnel/, 'Filter'],
  [/printer|print/, 'Print'],
  [/file-copy|clipboard|copy|file-paper/, 'Copy'],
  [/send-plane|mail-send|send/, 'Send'],
  [/arrow-left-double|skip-back|rewind/, 'First page'],
  [/arrow-right-double|skip-forward|fast-forward/, 'Last page'],
  [/arrow-left|arrow-go-back|chevron-left|back/, 'Previous'],
  [/arrow-right|arrow-go-forward|chevron-right/, 'Next'],
  [/arrow-up|chevron-up/, 'Collapse'],
  [/arrow-down|chevron-down/, 'Expand'],
  [/external-link|share-box|link/, 'Open link'],
  [/share/, 'Share'],
  [/attach|paperclip/, 'Attachment'],
  [/checkbox-circle|check-double|^check|task/, 'Confirm'],
  [/star/, 'Favorite'],
  [/question|help/, 'Help'],
  [/information|info/, 'Information'],
  [/error-warning|alert|warning/, 'Warning'],
  [/notification|bell/, 'Notifications'],
  [/history/, 'History'],
  [/time|clock|alarm/, 'Time'],
  [/calendar/, 'Calendar'],
  [/lock/, 'Locked'],
  [/user|account|team/, 'Account'],
  [/mail|inbox/, 'Email'],
  [/fullscreen/, 'Fullscreen'],
  [/moon/, 'Switch to dark mode'],
  [/sun/, 'Switch to light mode'],
  [/drag|draggable|move/, 'Reorder'],
  [/^pushpin|^pin|map-pin/, 'Pin'],
  [/file-list|file-text|file/, 'Document'],
  [/chat|message|discuss/, 'Comments'],
];

let counter = 0;
const nextId = prefix => `${prefix}-${++counter}`;

const text = el => (el.textContent || '').replace(/\s+/g, ' ').trim();

const isAuto = el => el.hasAttribute('data-a11y-auto');

const setAuto = (el, name, value) => {
  el.setAttribute(name, value);
  el.setAttribute('data-a11y-auto', '1');
};

const clearAuto = (el, name) => {
  if (!isAuto(el)) return;
  el.removeAttribute(name);
  el.removeAttribute('data-a11y-auto');
};

const iconLabel = el => {
  const icons = el.querySelectorAll(ICON);
  for (const icon of icons) {
    const cls = [...icon.classList].find(c => /^ri-.+-(line|fill)$/.test(c));
    if (!cls) continue;
    const bare = cls.replace(/^ri-/, '').replace(/-(line|fill)$/, '');
    const hit = ICON_LABELS.find(([re]) => re.test(bare));
    if (hit) return hit[1];
  }
  return '';
};

/* ---------------------------- icons ---------------------------- */

const hideDecorativeIcons = root => {
  root.querySelectorAll(ICON).forEach(icon => {
    if (
      icon.hasAttribute('aria-hidden') ||
      icon.hasAttribute('aria-label') ||
      icon.hasAttribute('role') ||
      icon.hasAttribute('title') ||
      text(icon)
    ) {
      return;
    }
    icon.setAttribute('aria-hidden', 'true');
  });
};

/* ---------------- buttons, links, menu items ------------------ */

const hasTextName = el => {
  if (el.hasAttribute('aria-labelledby')) return true;
  const clone = text(el);
  if (clone) return true;
  if (el.querySelector('img[alt]:not([alt=""])')) return true;
  if (el.querySelector('svg title')) return true;
  return false;
};

const nameInteractive = root => {
  const targets = root.matches?.(INTERACTIVE) ? [root] : [];
  root.querySelectorAll(INTERACTIVE).forEach(el => targets.push(el));
  targets.forEach(el => {
    if (el.tagName === 'INPUT') return;
    if (isAuto(el) && hasTextName(el)) {
      clearAuto(el, 'aria-label');
      clearAutoTip(el);
      return;
    }
    if (el.getAttribute('aria-label') && !isAuto(el)) {
      addTip(el, el.getAttribute('aria-label'));
      return;
    }
    if (el.hasAttribute('aria-labelledby') || hasTextName(el)) return;
    const title = el.getAttribute('title');
    const label = title || iconLabel(el);
    if (label) {
      setAuto(el, 'aria-label', label);
      addTip(el, label);
    }
  });
};

// Icon-only controls get a tooltip naming the action. Controls that already
// have a `title`, a reactstrap tooltip, or visible text are left alone, so
// buttons whose text already says what they do never get one.
const addTip = (el, label) => {
  if (el.hasAttribute('title') && !el.hasAttribute('data-a11y-auto-tip')) {
    return;
  }
  if (hasTextName(el) || /tooltip/i.test(el.id) || el.hasAttribute('data-tip')) {
    return;
  }
  el.setAttribute('title', label);
  el.setAttribute('data-a11y-auto-tip', '1');
};

const clearAutoTip = el => {
  if (!el.hasAttribute('data-a11y-auto-tip')) return;
  el.removeAttribute('title');
  el.removeAttribute('data-a11y-auto-tip');
};

/* ------------------------ form controls ------------------------ */

const isNamed = el => {
  if (el.hasAttribute('aria-labelledby')) return true;
  const own = el.getAttribute('aria-label');
  if (own && !isAuto(el)) return true;
  if (el.labels && el.labels.length) return true;
  return false;
};

const headerTextFor = cell => {
  const table = cell.closest('table');
  if (!table || !table.tHead) return '';
  const headRow = table.tHead.rows[table.tHead.rows.length - 1];
  const row = cell.parentElement;
  if (!headRow || headRow.cells.length !== row.cells.length) return '';
  return text(headRow.cells[cell.cellIndex]);
};

const rowTextFor = cell => {
  const row = cell.parentElement;
  for (const other of row.cells) {
    if (other === cell) continue;
    const t = text(other);
    if (t && t.length <= 48) return t;
  }
  return '';
};

const humanize = name =>
  String(name)
    .replace(/\[(\d+)\]/g, ' $1 ')
    .replace(/[._-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, c => c.toUpperCase());

const fallbackControlName = el => {
  const type = (el.getAttribute('type') || '').toLowerCase();
  const cell = el.closest('td, th');
  if (type === 'checkbox' || type === 'radio') {
    if (cell && cell.tagName === 'TH') return 'Select all rows';
    if (cell && cell.closest('tbody')) return 'Select row';
  }
  if (cell && cell.closest('table')) {
    const header = headerTextFor(cell);
    const row = rowTextFor(cell);
    if (header && row) return `${header}, ${row}`;
    if (header) return header;
  }
  const placeholder = el.getAttribute('placeholder');
  if (placeholder) return placeholder;
  if (el.getAttribute('title')) return el.getAttribute('title');
  const name = el.getAttribute('name');
  if (name && !/^react-select/.test(name)) return humanize(name);
  // react-select renders an unnamed combobox input
  const rs = el.closest('[class*="-container"]');
  const rsPlaceholder = rs && rs.querySelector('[class*="-placeholder"]');
  if (rsPlaceholder && text(rsPlaceholder)) return text(rsPlaceholder);
  return '';
};

const nameControls = root => {
  const controls = root.matches?.(CONTROL) ? [root] : [];
  root.querySelectorAll(CONTROL).forEach(el => controls.push(el));
  controls.forEach(el => {
    if (isAuto(el) && el.labels && el.labels.length) {
      clearAuto(el, 'aria-label');
      return;
    }
    if (isNamed(el)) return;
    const label = fallbackControlName(el);
    if (label) setAuto(el, 'aria-label', label);
  });
};

/* ----------------------------- labels ---------------------------- */

const firstControlIn = node => {
  if (!node || node.nodeType !== 1) return null;
  if (node.matches(CONTROL)) return node;
  return node.querySelector(CONTROL);
};

const associateLabels = root => {
  const labels = root.matches?.('label') ? [root] : [];
  root.querySelectorAll('label').forEach(l => labels.push(l));
  labels.forEach(label => {
    if (label.htmlFor || label.control || label.querySelector(CONTROL)) return;
    if (label.hasAttribute('data-a11y-linked')) return;
    if (!text(label)) return;

    let sib = label.nextElementSibling;
    for (let hop = 0; sib && hop < 3; hop += 1) {
      if (sib.tagName === 'LABEL') return;
      const grouped = sib.querySelectorAll(
        'input[type="radio"], input[type="checkbox"]'
      );
      if (grouped.length > 1) {
        if (!sib.hasAttribute('role') && !sib.hasAttribute('aria-labelledby')) {
          label.id = label.id || nextId('hy-a11y-label');
          sib.setAttribute('role', 'group');
          sib.setAttribute('aria-labelledby', label.id);
          label.setAttribute('data-a11y-linked', '1');
        }
        return;
      }
      const control = firstControlIn(sib);
      if (control) {
        if (control.labels && control.labels.length) return;
        if (control.hasAttribute('aria-labelledby')) return;
        if (control.hasAttribute('aria-label') && !isAuto(control)) return;
        control.id = control.id || nextId('hy-a11y-field');
        label.htmlFor = control.id;
        label.setAttribute('data-a11y-linked', '1');
        if (isAuto(control)) clearAuto(control, 'aria-label');
        return;
      }
      sib = sib.nextElementSibling;
    }
  });
};

/* ------------------------------ tables ----------------------------- */

const scopeHeaders = root => {
  root.querySelectorAll('th:not([scope])').forEach(th => {
    if (th.closest('thead')) {
      th.setAttribute('scope', (th.colSpan || 1) > 1 ? 'colgroup' : 'col');
    } else if (th.parentElement && th.parentElement.cells[0] === th) {
      th.setAttribute('scope', 'row');
    } else {
      th.setAttribute('scope', 'col');
    }
  });
};

/* --------------------------- dialogs, tabs -------------------------- */

const nameDialogs = root => {
  root
    .querySelectorAll('.modal[role="dialog"], .offcanvas, [role="dialog"]')
    .forEach(dialog => {
      if (
        dialog.hasAttribute('aria-label') ||
        dialog.hasAttribute('aria-labelledby')
      ) {
        return;
      }
      const title = dialog.querySelector(
        '.modal-title, .offcanvas-title, [class*="modal-title"], h1, h2, h3, h4, h5'
      );
      if (!title || !text(title)) return;
      title.id = title.id || nextId('hy-a11y-dialog-title');
      dialog.setAttribute('aria-labelledby', title.id);
    });
};

const TABLIST = 'ul.nav-tabs, ul.nav-pills, ul.nav-tabs-custom';

const syncTabState = list => {
  list.querySelectorAll('[role="tab"]').forEach(tab => {
    const active = tab.classList.contains('active');
    tab.setAttribute('aria-selected', active ? 'true' : 'false');
  });
};

const enhanceTabs = root => {
  const lists = root.matches?.(TABLIST) ? [root] : [];
  root.querySelectorAll(TABLIST).forEach(l => lists.push(l));
  lists.forEach(list => {
    if (list.hasAttribute('role')) return;
    const links = [...list.querySelectorAll(':scope > li > .nav-link')];
    // a nav-tabs used as plain navigation (real links) is not a tab widget
    if (
      !links.length ||
      links.some(a => a.getAttribute('href')?.startsWith('/'))
    ) {
      return;
    }
    list.setAttribute('role', 'tablist');
    list.querySelectorAll(':scope > li').forEach(li => {
      if (!li.hasAttribute('role')) li.setAttribute('role', 'presentation');
    });
    links.forEach(link => {
      if (link.hasAttribute('role')) return;
      link.setAttribute('role', 'tab');
      link.id = link.id || nextId('hy-a11y-tab');
      link.setAttribute('data-a11y-tab', '1');
    });
    syncTabState(list);

    // pair with the panes of the .tab-content that follows, when the counts match
    const content = list.nextElementSibling?.matches('.tab-content')
      ? list.nextElementSibling
      : list.parentElement?.querySelector(':scope > .tab-content');
    if (!content) return;
    const panes = [...content.querySelectorAll(':scope > .tab-pane')];
    if (panes.length !== links.length) return;
    panes.forEach((pane, i) => {
      if (pane.hasAttribute('role')) return;
      pane.id = pane.id || nextId('hy-a11y-tabpanel');
      pane.setAttribute('role', 'tabpanel');
      pane.setAttribute('aria-labelledby', links[i].id);
      links[i].setAttribute('aria-controls', pane.id);
    });
  });
};

/* ------------------------------- misc ------------------------------- */

const fixMedia = root => {
  root.querySelectorAll('img:not([alt])').forEach(img => {
    img.setAttribute('alt', '');
  });
  root.querySelectorAll('iframe:not([title])').forEach(frame => {
    frame.setAttribute('title', 'Embedded content');
  });
};

/* ------------------------------ driver ------------------------------ */

// Crude is a separate project: its markup is never touched.
const CRUDE_PATH = /^\/crude(\/|$)/;

const enhance = root => {
  if (!root || root.nodeType !== 1 || !root.isConnected) return;
  if (CRUDE_PATH.test(window.location.pathname)) return;
  hideDecorativeIcons(root);
  associateLabels(root);
  nameControls(root);
  nameInteractive(root);
  scopeHeaders(root);
  nameDialogs(root);
  enhanceTabs(root);
  fixMedia(root);
};

// Report what is still unnamed after the pass (development only).
export const auditAccessibleNames = (root = document.body) => {
  const missing = [];
  root.querySelectorAll(INTERACTIVE).forEach(el => {
    if (
      !el.getAttribute('aria-label') &&
      !el.hasAttribute('aria-labelledby') &&
      !hasTextName(el) &&
      !el.getAttribute('title')
    ) {
      missing.push({ kind: 'control', el });
    }
  });
  root.querySelectorAll(CONTROL).forEach(el => {
    if (!isNamed(el) && !el.getAttribute('aria-label') && !el.title) {
      missing.push({ kind: 'field', el });
    }
  });
  return missing;
};

/* ----------------------------- tooltips ----------------------------- */

// Native `title` tooltips only appear on mouse hover. Keyboard users get the
// same text in a small floating tip while the control has keyboard focus
// (WCAG 1.4.13: dismissable with Esc, never covers the control itself).
let tip = null;

const hideTip = () => {
  if (tip) tip.hidden = true;
};

const showTip = el => {
  const message = el.getAttribute('title') || el.getAttribute('data-tip');
  if (!message || message === text(el)) return;
  if (!tip || !tip.isConnected) {
    tip = document.createElement('div');
    tip.className = 'hy-a11y-tooltip';
    tip.setAttribute('role', 'tooltip');
    tip.setAttribute('aria-hidden', 'true'); // the title already names/describes it
    document.body.appendChild(tip);
  }
  tip.textContent = message;
  tip.hidden = false;
  const rect = el.getBoundingClientRect();
  const tipRect = tip.getBoundingClientRect();
  const gap = 8;
  let top = rect.bottom + gap;
  if (top + tipRect.height > window.innerHeight - 4) {
    top = Math.max(4, rect.top - tipRect.height - gap);
  }
  const left = Math.min(
    Math.max(4, rect.left + rect.width / 2 - tipRect.width / 2),
    window.innerWidth - tipRect.width - 4
  );
  tip.style.top = `${top}px`;
  tip.style.left = `${left}px`;
};

let started = false;

export const startA11yEnhancer = () => {
  if (started || typeof document === 'undefined' || !document.body) {
    return () => {};
  }
  started = true;

  const pending = new Set();
  let frame = 0;

  const flush = () => {
    frame = 0;
    const roots = [...pending];
    pending.clear();
    // one body-wide pass is cheaper than many overlapping sub-passes
    if (roots.length > 80) {
      enhance(document.body);
      return;
    }
    roots.forEach(enhance);
  };

  const schedule = node => {
    // re-scan the parent so a label that was already there can pick up a
    // control that has just been rendered next to it
    const root = node.parentElement || node;
    pending.add(root);
    if (!frame) frame = window.requestAnimationFrame(flush);
  };

  const observer = new MutationObserver(records => {
    for (const record of records) {
      record.addedNodes.forEach(node => {
        if (node.nodeType === 1) schedule(node);
      });
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
  enhance(document.body);

  // Tabs: keep aria-selected in step with the `.active` class, and give
  // role=tab the keys users expect (Space activates, arrows move focus).
  const onClick = event => {
    const list = event.target.closest?.('[role="tablist"]');
    if (list) window.setTimeout(() => syncTabState(list), 0);
  };
  const onKeyDown = event => {
    const tab = event.target.closest?.('[role="tab"][data-a11y-tab]');
    if (!tab) return;
    if (event.key === ' ') {
      event.preventDefault();
      tab.click();
      return;
    }
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const tabs = [
      ...tab.closest('[role="tablist"]').querySelectorAll('[role="tab"]'),
    ].filter(t => !t.classList.contains('disabled'));
    const index = tabs.indexOf(tab);
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    if (event.key === 'ArrowLeft')
      next = (index - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;
    event.preventDefault();
    tabs[next]?.focus();
  };
  const onFocusIn = event => {
    const el = event.target;
    if (el?.matches?.(':focus-visible') && el.hasAttribute('title')) {
      showTip(el);
    } else {
      hideTip();
    }
  };
  const onEscape = event => {
    if (event.key === 'Escape') hideTip();
  };
  document.addEventListener('click', onClick, true);
  document.addEventListener('keydown', onKeyDown, true);
  document.addEventListener('focusin', onFocusIn, true);
  document.addEventListener('focusout', hideTip, true);
  document.addEventListener('keydown', onEscape, true);
  window.addEventListener('scroll', hideTip, true);
  window.addEventListener('resize', hideTip);

  if (process.env.NODE_ENV !== 'production') {
    window.__a11yAudit = () => {
      const missing = auditAccessibleNames();
      // eslint-disable-next-line no-console
      console.table(
        missing.map(m => ({
          kind: m.kind,
          tag: m.el.tagName,
          class: m.el.className,
          html: m.el.outerHTML.slice(0, 120),
        }))
      );
      return missing.map(m => m.el);
    };
  }

  return () => {
    observer.disconnect();
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('keydown', onKeyDown, true);
    document.removeEventListener('focusin', onFocusIn, true);
    document.removeEventListener('focusout', hideTip, true);
    document.removeEventListener('keydown', onEscape, true);
    window.removeEventListener('scroll', hideTip, true);
    window.removeEventListener('resize', hideTip);
    if (tip) tip.remove();
    tip = null;
    if (frame) window.cancelAnimationFrame(frame);
    started = false;
  };
};
